import { rankings, rankItem } from "@tanstack/match-sorter-utils";
import type { ProjectDocumentFile } from "@t3tools/contracts";

/**
 * The hosted route for a file inside a project: the path relative to the
 * project's workspace root, each segment percent-encoded, under `/artifacts/`.
 * The project itself travels with the RPC call, not in the route.
 */
export function buildArtifactRoutePath(routePath: string): string {
  const encoded = routePath
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");
  return `/artifacts/${encoded}`;
}

export function filterDocumentFiles<T extends Pick<ProjectDocumentFile, "path">>(
  files: T[],
  query: string,
): T[] {
  const trimmed = query.trim();
  if (trimmed.length === 0) return files;
  return files.filter(
    (file) => rankItem(file.path, trimmed, { threshold: rankings.CONTAINS }).passed,
  );
}

/**
 * Pages across the project's documents, each distinct content counted once: a
 * file copied under another name carries the same pages, not new ones.
 */
export function countDocumentPages(
  files: ReadonlyArray<Pick<ProjectDocumentFile, "path" | "sha256" | "pageCount">>,
): number {
  const seen = new Set<string>();
  let pages = 0;
  for (const file of files) {
    if (file.pageCount === null) continue;
    const key = file.sha256 ?? `path:${file.path}`;
    if (seen.has(key)) continue;
    seen.add(key);
    pages += file.pageCount;
  }
  return pages;
}
